import type { CatalogEntry } from '../types';

/**
 * Settings (feature `05`) error codes + catalog.
 *
 * Code shape is `05AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * `Shared` codes (`0500xx`, AA=00) are recurring cross-endpoint conditions —
 * `WorkspaceNotFound`, `InsufficientWorkspaceRole`, `InviteNotFound`,
 * `MemberNotFound` — collapsed to ONE code each so the same condition reads the
 * same everywhere it can be raised. The workspace-role gate in particular fires
 * from nine endpoints (invite, list invites, resend, revoke, remove member,
 * update role, create link, list links, revoke link).
 *
 * Endpoint → `AA` map (controller order):
 *   getOrganization=01, updateOrganization=02, updateOrganizationTimezone=03,
 *   listWorkspaces=04, createWorkspace=05, updateWorkspace=06,
 *   deleteWorkspace=07, listMembers=08, inviteMember=09, listPendingInvites=10,
 *   resendInvite=11, revokeInvite=12, removeMember=13, updateMemberRole=14,
 *   createShareableLink=16, listShareableLinks=17, revokeShareableLink=18,
 *   getQuotas=19, updateQuota=20, listOrgMembers=21, updateOrgMemberRole=22,
 *   removeOrgMember=23, inviteOrgOwner=24, addMemberToWorkspace=25. AA=15
 *   (transferOwnership) is retired — the workspace transfer-ownership flow was
 *   removed; the slot is left vacant.
 *
 *   Org-members (21–23) are the owner-only Organization Members screen: list
 *   every member across all workspaces, promote/demote org owners, remove a
 *   member from the whole org. Gated on @RequiresOrgRole(OrgRole.OWNER).
 *
 * Per §5 (locked: prose-only) there are no structured error payloads here — all
 * messages are static prose. None of the settings throws carry dynamic detail.
 */
export const SETTINGS_CODES = {
  /** Cross-endpoint conditions (AA=00). */
  Shared: {
    WorkspaceNotFound: '050001',
    InsufficientWorkspaceRole: '050002',
    InviteNotFound: '050003',
    MemberNotFound: '050004',
  },
  /** getOrganization=01. */
  GetOrganization: {
    OrgNotFound: '050101',
  },
  /** deleteWorkspace=07. */
  DeleteWorkspace: {
    OnlyOrgOwnerCanDelete: '050701',
    CannotDeleteLast: '050702',
  },
  /** inviteMember=09. */
  InviteMember: {
    OnlyOrgAdminCanInviteAdmin: '050901',
    AlreadyMember: '050902',
    AlreadyInDifferentOrganization: '050903',
    AlreadyInvited: '050904',
  },
  /** removeMember=13. */
  RemoveMember: {
    OnlyOrgAdminCanRemoveAdmin: '051302',
    // Org owners can only be managed from the Organization Members screen.
    CannotRemoveOrgOwner: '051303',
  },
  /** updateMemberRole=14. */
  UpdateMemberRole: {
    CannotChangeOwnRole: '051401',
    OnlyOrgAdminCanManageAdmin: '051402',
    // Org owners can only be managed from the Organization Members screen.
    CannotChangeOrgOwnerRole: '051403',
  },
  /** createShareableLink=16. */
  CreateShareableLink: {
    OnlyOrgAdminCanCreateAdminLink: '051601',
  },
  /** revokeShareableLink=18. */
  RevokeShareableLink: {
    LinkNotFound: '051801',
  },
  /** updateQuota=20. */
  UpdateQuota: {
    ExceedsOrgAggregate: '052001',
  },
  /** updateOrgMemberRole=22 (promote-to-owner / demote-owner). */
  UpdateOrgMemberRole: {
    CannotModifySelf: '052201',
    CannotDemoteLastOwner: '052202',
    TargetWorkspaceRequired: '052203',
    TargetWorkspaceNotFound: '052204',
    MemberNotFound: '052205',
    // Demote is only valid against a current owner — guards the membership wipe.
    NotAnOwner: '052206',
  },
  /** removeOrgMember=23. */
  RemoveOrgMember: {
    CannotRemoveSelf: '052301',
    CannotRemoveLastOwner: '052302',
    MemberNotFound: '052303',
  },
  /**
   * inviteOrgOwner=24 — the Members-screen "Invite by email" card, always an OWNER
   * invite. Its own AA block rather than reusing InviteMember (09) because the
   * conditions differ: an existing org member is promoted here, not re-invited.
   */
  InviteOrgOwner: {
    AlreadyInvited: '052401',
    AlreadyOrgMember: '052402',
    AlreadyInDifferentOrganization: '052403',
    EmailSendFailed: '052404',
  },
  /**
   * addMemberToWorkspace=25 — Settings → People → "Add to a workspace": an
   * owner/admin adds an EXISTING org member directly to a specific workspace
   * with a role, no email-invite or shareable-link round trip. Owner is never
   * assignable through this endpoint (blocked at the DTO's `@IsEnum`) — see the
   * org-members promote-to-owner flow instead.
   */
  AddMemberToWorkspace: {
    AlreadyMember: '052501',
    // The tenant-scope check: the target userId must already belong to the
    // caller's organization (an `organizationMembers` row for the caller's
    // orgId) before it's written into one of that org's workspaces.
    UserNotFoundInOrg: '052502',
    // Same gate `inviteMember` (050901) and `updateMemberRole` (051402) apply
    // to the ADMIN role. All three routes grant the same privilege, so they
    // hold the same bar — see `SettingsService.addMemberToWorkspace`.
    OnlyOrgAdminCanAddAdmin: '052503',
  },
} as const;

export const SETTINGS_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared (cross-endpoint) ──
  '050001': { httpStatus: 404, message: "We couldn't find that workspace." },
  '050002': {
    httpStatus: 403,
    message: "You don't have permission to do that in this workspace.",
  },
  '050003': { httpStatus: 404, message: "We couldn't find that invite." },
  '050004': { httpStatus: 404, message: "We couldn't find that member." },

  // ── Get organization ──
  '050101': { httpStatus: 404, message: "We couldn't load your organization." },

  // ── Delete workspace ──
  '050701': {
    httpStatus: 403,
    message: 'Only an organization owner can delete a workspace.',
  },
  '050702': {
    httpStatus: 400,
    message: "You can't delete your only workspace. Create another one first.",
  },

  // ── Invite member ──
  '050901': {
    httpStatus: 403,
    message: 'Only an organization owner or admin can invite admins.',
  },
  '050902': { httpStatus: 409, message: 'This person is already a member of the workspace.' },
  '050903': {
    httpStatus: 409,
    message: "This person already belongs to a different organization and can't be invited here.",
  },
  '050904': {
    httpStatus: 409,
    message: 'An invite is already pending for this email address in this workspace.',
  },

  // ── Remove member ──
  '051302': {
    httpStatus: 403,
    message: 'Only an organization owner or admin can remove a workspace admin.',
  },
  '051303': {
    httpStatus: 403,
    message: 'Organization owners can only be managed from the Organization Members screen.',
  },

  // ── Update member role ──
  '051401': { httpStatus: 400, message: "You can't change your own role." },
  '051402': {
    httpStatus: 403,
    message: 'Only an organization owner or admin can assign or change the admin role.',
  },
  '051403': {
    httpStatus: 403,
    message: 'Organization owners can only be managed from the Organization Members screen.',
  },

  // ── Create shareable link ──
  '051601': {
    httpStatus: 403,
    message: 'Only an organization owner or admin can create an admin invite link.',
  },

  // ── Revoke shareable link ──
  '051801': { httpStatus: 404, message: "We couldn't find that invite link." },

  // ── Update quota ──
  '052001': {
    httpStatus: 400,
    message: "This allocation would exceed your organization's total daily warmup limit.",
  },

  // ── Update org member role (promote / demote owner) ──
  '052201': { httpStatus: 400, message: "You can't change your own organization role." },
  '052202': {
    httpStatus: 400,
    message: "You can't demote the last owner. Promote another member to owner first.",
  },
  '052203': {
    httpStatus: 400,
    message: 'Choose the workspace this member should remain an admin of.',
  },
  '052204': { httpStatus: 404, message: "We couldn't find that workspace." },
  '052205': { httpStatus: 404, message: "We couldn't find that organization member." },
  '052206': { httpStatus: 400, message: 'That member is not an owner.' },

  // ── Remove org member ──
  '052301': { httpStatus: 400, message: "You can't remove yourself from the organization." },
  '052302': {
    httpStatus: 400,
    message: "You can't remove the last owner. Promote another member to owner first.",
  },
  '052303': { httpStatus: 404, message: "We couldn't find that organization member." },

  // ── Invite org owner ──
  '052401': {
    httpStatus: 409,
    message: 'An invite is already pending for this email address.',
  },
  '052402': {
    httpStatus: 409,
    message:
      'This person is already in your organization. Promote them to owner from the members list instead.',
  },
  '052403': {
    httpStatus: 409,
    message: "This person already belongs to a different organization and can't be invited here.",
  },
  // 502, not 500: a downstream mail transport (or refused disposable domain), not
  // our bug. The invite row is revoked before this throws, so the address is
  // immediately retryable rather than blocked by its own orphan for 48h.
  '052404': {
    httpStatus: 502,
    message: "We couldn't send the invite email. Check the address and try again.",
  },

  // ── Add member to workspace ──
  '052501': { httpStatus: 409, message: 'This person is already a member of the workspace.' },
  '052502': {
    httpStatus: 404,
    message: "We couldn't find that person in your organization.",
  },
  '052503': {
    httpStatus: 403,
    message: 'Only an organization owner or admin can add admins.',
  },
};
